import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { Link } from "wouter";

const CONSENT_COOKIE = "cookie_consent";
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

type ConsentValue = "all" | "essential";

function getConsentCookie(): ConsentValue | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`)
  );
  if (!match) return null;
  const value = decodeURIComponent(match[1]);
  return value === "all" || value === "essential" ? value : null;
}

function setConsentCookie(value: ConsentValue) {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${CONSENT_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

export function hasMarketingConsent(): boolean {
  return getConsentCookie() === "all";
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getConsentCookie()) {
      setVisible(true);
    }
  }, []);

  // Push page content up so the banner doesn't obscure it
  useEffect(() => {
    if (visible) {
      document.body.style.paddingBottom = "130px";
    } else {
      document.body.style.paddingBottom = "";
    }
    return () => {
      document.body.style.paddingBottom = "";
    };
  }, [visible]);

  const accept = useCallback((value: ConsentValue) => {
    setConsentCookie(value);
    setVisible(false);

    if (value === "essential") {
      // Remove marketing cookie if user declines
      document.cookie =
        "double_o_attribution=; Max-Age=0; Path=/; SameSite=Lax";
      localStorage.removeItem("double-o-marketing-attribution");
    }
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 pointer-events-none">
      <div className="max-w-lg mx-auto bg-card border border-border rounded-lg shadow-lg p-4 pointer-events-auto">
        <p className="text-sm text-muted-foreground mb-3">
          We use essential cookies to keep the site working and optional cookies
          for marketing attribution. See our{" "}
          <Link
            to="/privacy"
            className="text-primary hover:underline"
          >
            Privacy Policy
          </Link>{" "}
          for details.
        </p>
        <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-3 justify-end">
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px] px-5"
            onClick={() => accept("essential")}
          >
            Essential Only
          </Button>
          <Button size="sm" className="min-h-[44px] px-5" onClick={() => accept("all")}>
            Accept All
          </Button>
        </div>
      </div>
    </div>
  );
}
